import { FormProvider, useForm } from "react-hook-form";

import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { api } from "~/utils/api";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { If } from "../Conditional";
import { Input } from "./Input";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./Dialog";
import { Button } from "./Button";
import { cn } from "~/utils/fontsConfig";
import GenericAuth from "~/components/auth/GenericAuth";
import LoginModal from "./LoginModal";
import Modal from "./Modal";
import { sanitizeEmail } from "~/utils/formatters";

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    event: EventBySlugOutput;
}

interface FormData {
    email: string;
    fullName: string;
}

const GuestRSVPLoginModal = ({ event, isOpen, setIsOpen }: Props) => {
    const form = useForm<FormData>();

    const [showVerify, setShowVerify] = useState(false);
    const [isGuestMode, setIsGuestMode] = useState(false);
    const [mode, setMode] = useState<"login" | "signup" | undefined>(undefined);

    const { data: questionnaireRequired } =
        api.questionnaire.isQuestionnaireRequired.useQuery({
            eventId: event.id as string,
        });

    const handleRegistration = async () => {
        const formData = form.getValues();

        // Convert the form data to a query string
        const queryString = Object.entries(formData)
            .map(
                ([key, value]) =>
                    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
            )
            .join("&");

        signIn("rsvp-email", {
            email: sanitizeEmail(form.getValues("email")),
            callbackUrl: `${window.location.origin}/events/${event?.slug}/rsvp-and-signup?${queryString}`,
            redirect: false,
        });
        setShowVerify(true);
    };

    if (showVerify) {
        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="bg-backgroundInvert">
                    <DialogHeader className="mb-4 text-[42px] font-bold leading-10 text-black">
                        Verify your email
                    </DialogHeader>
                    <DialogDescription className="text-gray-600">
                        Verify your email to confirm your Attendance for this
                        event.
                    </DialogDescription>
                    <DialogFooter>
                        <Button
                            className="text-background hover:text-tertiary"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            Back
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            <Modal
                open={isOpen}
                onOpenChange={setIsOpen}
                title="Log in or Sign up to Attend"
            >
                <If condition={!questionnaireRequired}>
                    <If condition={!mode}>
                        <DialogDescription>
                            <div className="flex flex-col items-center gap-4 text-black">
                                <div className="flex w-full justify-center space-x-2">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setMode("signup");
                                        }}
                                        className="w-full max-w-[140px]"
                                    >
                                        Sign up
                                    </Button>
                                    <Button
                                        variant="default"
                                        onClick={() => {
                                            setMode("login");
                                        }}
                                        className="w-full max-w-[140px]"
                                    >
                                        Log in
                                    </Button>
                                </div>
                            </div>
                        </DialogDescription>

                        <div className="my-4 h-[1px] w-full bg-white" />

                        <p className="mb-4 text-center text-xl text-white">
                            {/* Attend with your name and email */}
                            or{" "}
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setIsGuestMode(!isGuestMode);
                                }}
                                className="ml-2 w-full max-w-[140px]"
                            >
                                Attend as Guest
                            </Button>
                        </p>
                    </If>
                    {mode !== undefined && (
                        <GenericAuth
                            mode={mode}
                            variant="modal"
                            callbackUrl={`/events/${event.slug}?registrationPhase=continue`}
                        />
                    )}
                    <div
                        className={cn(
                            "flex flex-col gap-3",
                            "max-h-0 overflow-hidden transition-all delay-150 ease-in-out",
                            isGuestMode && "max-h-52",
                        )}
                    >
                        <FormProvider {...form}>
                            <form className="flex flex-col gap-3">
                                <div>
                                    <label>Email *</label>
                                    <Input
                                        className="bg-transparent"
                                        {...form.register("email", {
                                            setValueAs: (value: string) =>
                                                sanitizeEmail(value),
                                        })}
                                        placeholder="myemail@email.com"
                                    />
                                </div>
                                <div>
                                    <label>Full name *</label>
                                    <Input
                                        className="bg-transparent"
                                        {...form.register("fullName")}
                                        placeholder="Your name"
                                    />
                                </div>
                            </form>
                        </FormProvider>

                        <DialogFooter className="mb-3 mr-1 flex flex-col items-center gap-2 xs:flex-row">
                            <Button
                                variant="secondary"
                                className="w-full max-w-[140px]"
                                onClick={() => {
                                    setIsOpen(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="default"
                                onClick={handleRegistration}
                            >
                                Send Guest Link
                            </Button>
                        </DialogFooter>
                    </div>
                </If>
            </Modal>
            <If condition={!!questionnaireRequired}>
                <LoginModal
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    title="Log in to Attend"
                    description="This event has an obligatory questionnaire. To answer it you must be logged in."
                    callbackUrl={`/events/${event.slug}?registrationPhase=continue`}
                />
            </If>
        </>
    );
};

export default GuestRSVPLoginModal;
